import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchJobListings } from "../../Redux/WebsiteSlice";
import { workWithUsAPI } from "../../services/api";
import { Link } from "react-router-dom";
import PageAboutSection from "../Shared/PageAboutSection";
import Reveal from "../Shared/Reveal";
import "./WorkWithUs.scss";

const ICONS = {
  people: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    </svg>
  ),
  growth: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
    </svg>
  ),
  heart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  ),
  screen: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  ),
  money: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2"></rect>
      <line x1="9" y1="22" x2="9" y2="12"></line>
      <line x1="15" y1="22" x2="15" y2="12"></line>
      <rect x="9" y="7" width="6" height="5"></rect>
    </svg>
  ),
  award: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="6"></circle>
      <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
    </svg>
  ),
};

const WorkWithUs = () => {
  const dispatch = useDispatch();
  const jobListings = useSelector((state) => state.website.jobListings);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    dispatch(fetchJobListings());
    workWithUsAPI.getContent()
      .then(res => setPageContent(res.data))
      .catch(() => {});
  }, [dispatch]);

  const hero = pageContent?.hero;
  const valuesSection = pageContent?.valuesSection;
  const values = pageContent?.values?.filter(v => v.isActive) || [];
  const cta = pageContent?.cta;

  return (
    <div className="page-wwu">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">{hero?.label || 'JOIN OUR TEAM'}</span>
          <h1 className="headline__text">
            {hero?.title || 'Work With Us'}
          </h1>
          <p className="page-hero__subtitle">
            {hero?.subtitle || "We're always looking for talented, passionate people to join our team and help us deliver exceptional care."}
          </p>
        </div>
      </div>

      <PageAboutSection
        pageKey="workWithUs"
        fallbackHeading="Build Your Career With Us"
        fallbackParagraph="We're always looking for talented, passionate people to join our team and help us deliver exceptional care to our community."
      />

      {values.length > 0 && (
        <div className="wwu-values">
          <div className="wwu-values__inner">
            <div className="wwu-values__heading">
              <span className="small__text">{valuesSection?.label || 'WHY INTEGRITY PHYSIO?'}</span>
              <h2 className="headline__text">
                {valuesSection?.title || 'A Great Place to Work'}
              </h2>
              <p>{valuesSection?.subtitle || "We're committed to building a workplace where every team member feels valued, challenged, and supported."}</p>
            </div>
            <Reveal as="div" direction="up" className="wwu-values__grid">
              {values.map((val, i) => (
                <div key={val._id || i} className="wwu-value-card">
                  <div className="wwu-value-card__icon">
                    {ICONS[val.icon] || ICONS.people}
                  </div>
                  <h3 className="wwu-value-card__title">{val.title}</h3>
                  <p className="wwu-value-card__desc">{val.description}</p>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      )}

      <div className="wwu-roles">
        <div className="wwu-roles__inner">
          <div className="wwu-roles__heading">
            <span className="small__text">CURRENT OPENINGS</span>
            <h2 className="headline__text">
              Open <span>Positions</span>
            </h2>
          </div>
          <Reveal as="div" direction="up" className="wwu-roles__list">
            {jobListings.length === 0 ? (
              <div className="wwu-roles__empty">
                <p>No positions currently available. Check back soon or get in touch to express your interest.</p>
              </div>
            ) : (
              jobListings.map((role) => (
                <div key={role._id || role.title} className="wwu-role-card">
                  <div className="wwu-role-card__header">
                    <div>
                      <h3 className="wwu-role-card__title">{role.title}</h3>
                      <span className="wwu-role-card__type">{role.type}</span>
                    </div>
                    <Link to="/contact" className="site__button site__button_style--solid site__button_shape--default">
                      <span className="text">Apply Now</span>
                    </Link>
                  </div>
                  <p className="wwu-role-card__desc">{role.description}</p>
                </div>
              ))
            )}
          </Reveal>
        </div>
      </div>

      <div className="wwu-apply">
        <Reveal as="div" direction="up" className="wwu-apply__inner">
          <h2>{cta?.title || "Don't see a suitable role?"}</h2>
          <p>{cta?.description || "We're always interested in hearing from motivated healthcare professionals. Send us your resume and a brief introduction."}</p>
          <Link to="/contact" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Get in Touch</span>
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default WorkWithUs;
